import type {
  EditorMarkerManager,
  EditorMarkingStyle,
  TeoMarker,
} from './EditorMarkerManager.svelte';

export type EditorMarking = {
  id: string;
  markerId: number;
  rect: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
  color: string;
  hoverColor?: string;
  focusColor?: string;
  underlineColor?: string;
  type: EditorMarkingStyle;
  animate?: boolean;
  clickable?: boolean;
  icon?: boolean;
};

export class EditorMarkings {
  idCounter = 0;
  currentMarkers: TeoMarker[] = [];
  current: EditorMarking[] = $state([]);
  hoveredMarkerId?: number = $state();
  focusedMarkerIds: number[] = $state([]);
  editedMarkerIds: number[] = $state([]);
  editorPadding = 40;
  clickableMarkers: string[] = [];
  // editorContainer: HTMLElement | null = null;
  constructor(
    private markers: EditorMarkerManager,
    private container: HTMLElement,
  ) {}

  get editorContent() {
    return this.markers.editorContent;
  }

  setFocusedMarkers(markerIds: number[]) {
    this.focusedMarkerIds = markerIds;
  }

  addEditedMarker(markerId: number) {
    if (!this.editedMarkerIds.includes(markerId)) {
      this.editedMarkerIds.push(markerId);
    }
    console.log('Edited markers:', this.editedMarkerIds);
  }

  clearEditedMarkers() {
    this.editedMarkerIds.length = 0;
  }

  showMarkers(markers: TeoMarker[], interactiveMarkers: string[] = []) {
    this.clickableMarkers = interactiveMarkers;
    if (!this.editorContent) return;
    this.currentMarkers.length = 0;
    markers.forEach((marker) => {
      if (!this.currentMarkers.some((existingMarker) => existingMarker.id === marker.id)) {
        this.currentMarkers.push(marker);
      }
    });
    this.showCurrentMarkers(true);
  }

  private showCurrentMarkers(animate = false) {
    if (!this.editorContent) return;
    this.current = [];
    this.currentMarkers.forEach((marker) => {
      const elements: HTMLElement[] = this.editorContent!.getTextElements(marker.id);
      if (marker.style === 'underline' || marker.style === 'background') {
        const newMarkings: EditorMarking[] = this.createInlineMarkings(marker, elements, animate);
        this.current.push(...newMarkings);
      } else {
        const blockMarking = this.createBlockMarking(marker, elements, animate);
        if (blockMarking) {
          this.current.push(blockMarking);
        }
      }
    });
  }

  hideMarkers() {
    this.currentMarkers.length = 0;
    this.current.length = 0;
  }

  reRender() {
    // if (!this.container) return;
    this.showCurrentMarkers();
  }

  clickMarker(markerId: number) {
    this.markers.clickMarker(markerId);
  }

  private createBlockMarking(
    marker: TeoMarker,
    elements: HTMLElement[],
    animate: boolean,
  ): EditorMarking | undefined {
    const bgRect: DOMRect = this.container.getBoundingClientRect();
    const rects: DOMRect[] = this.extractRects(elements);
    if (!rects.length) return;
    const firstRect: DOMRect = rects[0] as DOMRect;
    const lastRect: DOMRect = rects[rects.length - 1] as DOMRect;
    const marking = this.createMarking(marker, firstRect, bgRect, animate, true);
    marking.rect.left = this.editorPadding;
    marking.rect.width = bgRect.width - this.editorPadding * 2;
    marking.rect.height = lastRect.bottom - firstRect.top;
    return marking;
  }

  private createInlineMarkings(
    marker: TeoMarker,
    elements: HTMLElement[],
    animate: boolean,
  ): EditorMarking[] {
    const bgRect: DOMRect = this.container.getBoundingClientRect();
    const rects: DOMRect[] = this.extractRects(elements);
    const result = [];
    let currentTop = 0;
    let lastLeftEnd = 0;
    let currentMarking = null;
    let isFirst = true;
    for (const rect of rects) {
      if (rect.top === currentTop && rect.left !== lastLeftEnd && currentMarking) {
        console.warn('Unexpected gap in inline marking');
      }
      if ((rect.top !== currentTop || rect.left !== lastLeftEnd) && currentMarking) {
        result.push(currentMarking);
        currentMarking = null;
      }
      if (currentMarking) {
        currentMarking.rect.width += rect.width;
      } else {
        currentMarking = this.createMarking(marker, rect, bgRect, animate, isFirst);
      }
      currentTop = rect.top;
      lastLeftEnd = rect.left + rect.width;
      isFirst = false;
    }
    if (currentMarking) {
      result.push(currentMarking);
    }
    return result;
  }

  private extractRects(elements: HTMLElement[]): DOMRect[] {
    const rects: DOMRect[] = [];
    elements.forEach((element) => {
      const rectList: DOMRectList = element.getClientRects();
      // DOMRectList handles differently from an array
      for (let i = 0; i < rectList.length; i++) {
        const item = rectList.item(i);
        if (item) rects.push(item);
      }
    });
    // Sort by top and left to ensure consistent ordering
    rects.sort((a, b) => {
      if (a.top !== b.top) {
        return a.top - b.top;
      }
      return a.left - b.left;
    });
    return rects;
  }

  private createMarking(
    marker: TeoMarker,
    rect: DOMRect,
    bgRect: DOMRect,
    animate: boolean,
    isFirst: boolean = false,
  ): EditorMarking {
    return {
      id: `marker-${marker.id}-${this.idCounter++}`,
      markerId: marker.id,
      rect: {
        top: rect.top - bgRect.top,
        left: rect.left - bgRect.left,
        width: rect.width,
        height: rect.height,
      },
      color: marker.color,
      hoverColor: marker.hoverColor,
      focusColor: marker.hoverColor,
      underlineColor: marker.underlineColor,
      clickable: this.clickableMarkers.includes(marker.type),
      type: marker.style,
      animate,
      icon: (marker.icon && isFirst) || false,
    };
  }
}
